import { onBeforeUnmount, onMounted, Ref } from 'vue';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), '
  + 'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isFocusable(el: HTMLElement | null): el is HTMLElement {
  return !!el && document.body.contains(el) && !el.hasAttribute('disabled');
}

export function useDialogFocusTrap(dialogRef: Ref<HTMLElement | null>): void {
  let previouslyFocused: HTMLElement | null = null;

  const getFocusableElements = (): HTMLElement[] => {
    if (!dialogRef.value) return [];
    return Array.from(dialogRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  };

  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!dialogRef.value?.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  onMounted(() => {
    previouslyFocused = document.activeElement as HTMLElement | null;
    const focusable = getFocusableElements();
    (focusable[0] ?? dialogRef.value)?.focus();
    document.addEventListener('keydown', handleKeydown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
    if (isFocusable(previouslyFocused)) {
      previouslyFocused.focus();
    }
  });
}
